import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, FileText, Trash2, Pencil, Camera, Images } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { format, parseISO } from "date-fns";
import PhotoGallery from "@/components/PhotoGallery";

const STATUS_CLASS = {
  draft: "bm-badge-draft",
  sent: "bm-badge-sent",
  paid: "bm-badge-paid",
};

const emptyItem = { description: "", quantity: 1, unit_price: 0 };

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [selCustomer, setSelCustomer] = useState("");
  const [selJobIds, setSelJobIds] = useState([]);
  const [lineItems, setLineItems] = useState([{ ...emptyItem }]);
  const [notes, setNotes] = useState("");
  const [invoicePhotosCount, setInvoicePhotosCount] = useState(0);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const [inv, cs, js, st] = await Promise.all([
        api.get("/invoices"),
        api.get("/customers"),
        api.get("/jobs", { params: { status_f: "completed" } }),
        api.get("/settings"),
      ]);
      setInvoices(inv.data);
      setCustomers(cs.data);
      setCompletedJobs(js.data);
      setServiceTypes(st.data?.service_types || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (loading) return;
    const customerId = searchParams.get("customer");
    const editId = searchParams.get("edit");
    if (editId) {
      (async () => {
        try {
          const { data } = await api.get(`/invoices/${editId}`);
          openEdit(data);
        } catch {
          toast.error("Could not load invoice");
        } finally {
          const next = new URLSearchParams(searchParams);
          next.delete("edit");
          setSearchParams(next, { replace: true });
        }
      })();
    } else if (customerId) {
      openGenerateForCustomer(customerId);
      const next = new URLSearchParams(searchParams);
      next.delete("customer");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchParams.toString()]);

  const customerJobs = completedJobs.filter((j) => j.customer_id === selCustomer);

  const resetForm = () => {
    setEditingId(null);
    setSelCustomer("");
    setSelJobIds([]);
    setLineItems([{ ...emptyItem }]);
    setNotes("");
    setInvoicePhotosCount(0);
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const refreshInvoicePhotosCount = async (invId) => {
    try {
      const { data } = await api.get(`/invoices/${invId}/photos`);
      setInvoicePhotosCount(data.length);
    } catch {
      setInvoicePhotosCount(0);
    }
  };

  const openGenerateForCustomer = (customerId) => {
    resetForm();
    setSelCustomer(customerId);
    const invoicedJobIds = new Set(invoices.flatMap((inv) => inv.job_ids || []));
    const uninvoiced = completedJobs.filter(
      (j) => j.customer_id === customerId && !invoicedJobIds.has(j.id)
    );
    if (uninvoiced.length > 0) {
      setSelJobIds(uninvoiced.map((j) => j.id));
      setLineItems(
        uninvoiced.map((j) => ({
          description: `${j.service_type} — ${format(parseISO(j.scheduled_date), "MMM d, yyyy")}`,
          quantity: 1,
          unit_price: Number(j.price) || 0,
        }))
      );
    }
    setOpen(true);
  };

  const openEdit = (inv) => {
    setEditingId(inv.id);
    setSelCustomer(inv.customer_id);
    setSelJobIds(inv.job_ids || []);
    setLineItems(
      (inv.line_items || []).map((li) => ({
        description: li.description || "",
        quantity: Number(li.quantity) || 0,
        unit_price: Number(li.unit_price) || 0,
      }))
    );
    setNotes(inv.notes || "");
    refreshInvoicePhotosCount(inv.id);
    setOpen(true);
  };

  const toggleJob = (j) => {
    if (selJobIds.includes(j.id)) {
      setSelJobIds(selJobIds.filter((id) => id !== j.id));
    } else {
      setSelJobIds([...selJobIds, j.id]);
      const placeholder = `${j.service_type} — ${format(parseISO(j.scheduled_date), "MMM d, yyyy")}`;
      if (!lineItems.some((li) => li.description === placeholder)) {
        setLineItems((prev) => [
          ...prev.filter((li) => li.description || li.unit_price),
          { description: placeholder, quantity: 1, unit_price: Number(j.price) || 0 },
        ]);
      }
    }
  };

  const addItem = () => setLineItems([...lineItems, { ...emptyItem }]);
  const removeItem = (idx) => setLineItems(lineItems.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => {
    const next = [...lineItems];
    next[idx] = { ...next[idx], [field]: field === "description" ? val : Number(val) };
    setLineItems(next);
  };

  const total = lineItems.reduce(
    (s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0),
    0
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selCustomer) { toast.error("Select a customer"); return; }
    const items = lineItems.filter((li) => li.description || li.unit_price);
    if (items.length === 0) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: selCustomer,
        job_ids: selJobIds,
        line_items: items.map(({ description, quantity, unit_price }) => ({
          description,
          quantity: Number(quantity) || 0,
          unit_price: Number(unit_price) || 0,
        })),
        tax_rate: 0,
        notes,
      };
      if (editingId) {
        await api.put(`/invoices/${editingId}`, payload);
        toast.success("Invoice updated");
      } else {
        const { data } = await api.post("/invoices", payload);
        toast.success(`Invoice ${data.number} created`);
      }
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteInvoice = async (id, num) => {
    if (!window.confirm(`Delete ${num}?`)) return;
    try { await api.delete(`/invoices/${id}`); toast.success("Deleted"); load(); } catch {}
  };

  return (
    <div data-testid="invoices-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Billing</div>
          <h1 className="font-display text-4xl font-black tracking-tight leading-none">Invoices</h1>
        </div>
        <button onClick={openCreate} className="bm-btn bm-btn-primary" data-testid="add-invoice-button">
          <Plus className="w-4 h-4" /> New Invoice
        </button>
      </div>

      {loading ? (
        <div className="overline">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="bm-card p-10 text-center text-[#8ba394]" data-testid="invoices-empty">
          <FileText className="w-8 h-8 mx-auto mb-3 text-[#233129]" />
          <div className="mb-4">No invoices yet.</div>
          <button onClick={openCreate} className="bm-btn bm-btn-outline">
            <Plus className="w-4 h-4" /> Create your first
          </button>
        </div>
      ) : (
        <div className="bm-card overflow-hidden" data-testid="invoices-table">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b border-[#233129]">
                <th className="overline p-4">Number</th>
                <th className="overline p-4">Customer</th>
                <th className="overline p-4 hidden md:table-cell">Issued</th>
                <th className="overline p-4">Total</th>
                <th className="overline p-4">Status</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b border-[#233129] hover:bg-[#1d2823] transition-colors"
                  data-testid={`invoice-row-${inv.id}`}
                >
                  <td className="p-4 font-mono font-bold text-[#e56b42]">
                    <Link to={`/invoices/${inv.id}`}>{inv.number}</Link>
                  </td>
                  <td className="p-4">{inv.customer_name}</td>
                  <td className="p-4 hidden md:table-cell font-mono text-sm text-[#b0c0b6]">{inv.issued_date}</td>
                  <td className="p-4 font-mono font-bold">${Number(inv.total).toFixed(2)}</td>
                  <td className="p-4">
                    <span className={`bm-badge ${STATUS_CLASS[inv.status]}`}>{inv.status}</span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center gap-3 justify-end">
                      <button onClick={() => openEdit(inv)} className="text-[#8ba394] hover:text-[#eae6db]" data-testid={`edit-invoice-${inv.id}`} title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteInvoice(inv.id, inv.number)} className="text-[#8ba394] hover:text-[#c83e2d]" data-testid={`delete-invoice-${inv.id}`} title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="bg-[#141c18] border border-[#233129] text-[#eae6db] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">
              {editingId ? "Edit Invoice" : "New Invoice"}
            </DialogTitle>
            <DialogDescription className="text-[#8ba394] text-sm">
              {editingId ? "Update line items, photos, or notes on this invoice." : "Add line items to generate a new invoice."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="invoice-form">
            <div>
              <label className="overline block mb-2">Customer *</label>
              <Select value={selCustomer} onValueChange={setSelCustomer} disabled={!!editingId}>
                <SelectTrigger className="bm-input" data-testid="invoice-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent className="bg-[#141c18] border-[#233129] text-[#eae6db]">
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {editingId && <p className="text-xs text-[#8ba394] mt-1">Customer cannot be changed on an existing invoice.</p>}
            </div>

            {selCustomer && customerJobs.length > 0 && (
              <div>
                <label className="overline block mb-2">Completed Jobs (auto-add to invoice)</label>
                <div className="max-h-32 overflow-auto space-y-1 border border-[#233129] p-2 rounded-sm">
                  {customerJobs.map((j) => (
                    <label key={j.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                      <input type="checkbox" checked={selJobIds.includes(j.id)} onChange={() => toggleJob(j)} />
                      <span className="font-mono text-xs text-[#8ba394]">{j.scheduled_date}</span>
                      <span>{j.service_type}</span>
                      {j.price > 0 && <span className="ml-auto font-mono text-[#e56b42]">${j.price}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Line Items</label>
                <button type="button" onClick={addItem} className="bm-btn bm-btn-ghost text-xs">
                  <Plus className="w-3 h-3" /> Add row
                </button>
              </div>
              <div className="grid grid-cols-[1fr_60px_80px_36px] gap-2 px-1 pb-1 border-b border-[#233129] mb-2">
                <span className="overline text-[10px]">Service / Description</span>
                <span className="overline text-[10px] text-right">Qty</span>
                <span className="overline text-[10px] text-right">Price ($)</span>
                <span></span>
              </div>
              <datalist id="invoice-service-types">
                {serviceTypes.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              <div className="space-y-2">
                {lineItems.map((li, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_60px_80px_36px] gap-2 items-center">
                    <input
                      className="bm-input"
                      list="invoice-service-types"
                      placeholder="Pick a service or type custom"
                      value={li.description}
                      onChange={(e) => updateItem(idx, "description", e.target.value)}
                      data-testid={`invoice-item-desc-${idx}`}
                    />
                    <input className="bm-input" type="number" step="0.5" placeholder="Qty" value={li.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`invoice-item-qty-${idx}`} />
                    <input className="bm-input" type="number" step="0.01" placeholder="Price" value={li.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} data-testid={`invoice-item-price-${idx}`} />
                    <button type="button" onClick={() => removeItem(idx)} className="text-[#8ba394] hover:text-[#c83e2d] p-2" aria-label="Remove row">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end items-center bg-[#0a0f0d] border border-[#233129] p-3 rounded-sm">
              <div className="text-lg font-display font-black text-[#e56b42] flex items-center gap-3">
                <span className="overline text-[#8ba394]">Total</span>
                <span className="font-mono">${total.toFixed(2)}</span>
              </div>
            </div>

            {editingId && (
              <div className="border border-[#233129] p-3 rounded-sm" data-testid="invoice-photos-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Images className="w-4 h-4 text-[#e56b42]" />
                    <span className="overline">Invoice Photos</span>
                    {invoicePhotosCount > 0 && (
                      <span className="bm-badge bm-badge-sent">{invoicePhotosCount}</span>
                    )}
                  </div>
                  <button type="button" onClick={() => setPhotosOpen(true)} className="bm-btn bm-btn-secondary" data-testid="manage-invoice-photos-button">
                    <Camera className="w-4 h-4" /> Manage Photos
                  </button>
                </div>
                <p className="text-xs text-[#8ba394] mt-2">
                  Photos attached here appear in the invoice PDF and the customer portal.
                </p>
              </div>
            )}

            <div>
              <label className="overline block mb-2">Notes</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="bm-input" placeholder="Terms, thank you, payment instructions..." />
            </div>

            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="bm-btn bm-btn-outline">Cancel</button>
              <button type="submit" disabled={saving} className="bm-btn bm-btn-primary" data-testid="invoice-submit-button">
                {saving ? "Saving…" : editingId ? "Save Changes" : "Create Invoice"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {editingId && (
        <PhotoGallery
          open={photosOpen}
          onOpenChange={setPhotosOpen}
          target={{ type: "invoice", id: editingId }}
          customerName="Invoice"
          onChanged={() => refreshInvoicePhotosCount(editingId)}
        />
      )}
    </div>
  );
}
